import {Device as CapacitorDevice} from "@capacitor/device";
import {Component, computed, inject, OnDestroy, signal} from '@angular/core';
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {NgClass, SlicePipe} from '@angular/common';
import {BackButtonComponent} from "src/app/presentation/main-view/back-button/back-button.component";
import {CloseButtonComponent} from "src/app/presentation/main-view/close-button/close-button.component";
import {DeviceStore} from 'src/app/domain/devices/device.store';
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {CapacitorNfc, PluginListenerHandle, StartScanningOptions} from "@capgo/capacitor-nfc";
import {Samples} from 'src/app/infrastructure/moovement-api/models/samples';
import {
  InternalNotificationsStore,
  InternalNotificationType
} from 'src/app/infrastructure/internal-notifications/internal-notifications.store';
import {Animal, AnimalType, Device} from 'src/app/infrastructure/moovement-api/models/asset';
import {AnimalsStore} from 'src/app/domain/assets/animals/animals.store';
import {NetworkStatusStore} from 'src/app/infrastructure/network/network-status.store';
import {ToCapitalizedTextPipe} from "src/app/presentation/common/pipes/ToCapitalizedTextPipe";
import {randomUuid} from 'src/app/domain/util/uuid/uuid';
import {environment} from "src/app/environments/environment";
import {appVersion} from "src/app/app-info";
import {DataSynchronisationService} from "src/app/infrastructure/data-synchronisation.service";
import {ScheduledUpdate, ScheduledUpdates} from "src/app/infrastructure/scheduled-updates/scheduled-updates";
import {Share} from "@capacitor/share";
import {toSampleValues} from "src/app/presentation/main-view/read-nfc-tag/to-sample-values";
import {Geolocation} from "@capacitor/geolocation";
import {UserLocationStore} from "src/app/domain/user/user-location.store";
import L from "leaflet";
import Position = Samples.Position;

@Component({
  selector: 'app-read-nfc-tag',
  imports: [
    NgClass,
    BackButtonComponent,
    CloseButtonComponent,
    GenericButtonComponent,
    SlicePipe,
    ToCapitalizedTextPipe
  ],
  templateUrl: './read-nfc-tag.component.html',
  styleUrl: './read-nfc-tag.component.css'
})
export class ReadNfcTagComponent implements OnDestroy {

  loading = computed(() => this.deviceStore.loading());
  device = signal<Device | undefined>(undefined);
  animal = signal<Animal | undefined>(undefined);
  sampleValues = signal<Samples.SampleValue<Samples.NfcGpsFix>[]>([]);
  discrepancyReason = signal<string | null>(null);

  showAnimalCreationForm = signal(false);
  canCreateAnimal = computed(() => this.name() !== undefined);

  years = signal<number[]>([]);
  animalTypes = signal<AnimalType[]>([AnimalType.COW, AnimalType.STEER]);

  name = signal<string | undefined>(undefined);
  animalType = signal<string | undefined>(undefined);
  year = signal<string | undefined>(undefined);

  canProcessLocations = computed(() => this.sampleValues().length > 0);

  networkStatusStore = inject(NetworkStatusStore);
  private userLocationStore = inject(UserLocationStore);
  scanning = signal(false);
  listener: PluginListenerHandle | undefined = undefined;

  hasNativeScanningDialog = signal(true);

  private shouldCreateAnimal = false;

  private readonly scanningOptions: StartScanningOptions = {
    alertMessage: 'Hold your device near an NFC tag',
    iosSessionType: 'tag',
    iosPollingOptions: ['iso15693'],
    androidReaderModeFlags: 8
  };

  private readonly animalStore = inject(AnimalsStore);
  private readonly deviceStore = inject(DeviceStore);
  private readonly internalNotificationsStore = inject(InternalNotificationsStore);

  constructor(
    private readonly dataSynchronisationService: DataSynchronisationService,
    protected readonly viewportService: ViewportService
  ) {
    const today = new Date();
    const years = [];
    for (let i = 0; i < 10; i++) {
      years.push(today.getFullYear() - i)
    }
    this.years.set(years);

    this.init();
  }

  private async init() {
    const info = await CapacitorDevice.getInfo();
    const operatingSystem = info.operatingSystem;

    this.hasNativeScanningDialog.set(operatingSystem === 'ios');
  }

  async ngOnDestroy(): Promise<void> {
    await this.stopScan();
  }

  async stopScan() {
    await CapacitorNfc.stopScanning();
    if (this.listener) await this.listener.remove();
    this.reset();
  }

  async startScan() {
    this.reset();

    const isSupported = await CapacitorNfc.isSupported();
    if (!isSupported.supported) {
      this.internalNotificationsStore.addNotification(
        "NFC is not supported on this device.", InternalNotificationType.ERROR, true
      );
      return;
    }

    const status = await CapacitorNfc.getStatus();
    if (status.status !== 'NFC_OK') {
      this.internalNotificationsStore.addNotification(
        "NFC is not enabled on this device. Please enable NFC and try again.", InternalNotificationType.ERROR, true
      );
      return;
    }

    try {
      await this.setUpNfcListener();

      await CapacitorNfc.startScanning(this.scanningOptions);

      this.scanning.set(true);
    } catch (error) {
      console.error('Failed to start NFC scanning:', error);
    }
  }

  toggleShowAnimalCreationForm() {
    this.showAnimalCreationForm.set(!this.showAnimalCreationForm());
  }

  private reset() {
    this.device.set(undefined);
    this.animal.set(undefined);
    this.sampleValues.set([]);
    this.showAnimalCreationForm.set(false);
    this.scanning.set(false);
  }

  private async setUpNfcListener() {
    this.listener = await CapacitorNfc.addListener('nfcEvent', async (event) => {
      await this.stopScan();

      const id = event.tag.id;
      if (!id) {
        const message = `Unable to read ID from NFC tag.`
        const techDetails = `Raw event payload: \n ${JSON.stringify(event)}`
        console.error(message);
        this.displayUnreadableTagNotification(message, techDetails);
        this.offerRetry();
        return;
      }

      const hardwareId = await this.normalizedHardwareId(id);
      const device = this.deviceStore.entities().find(device => device.hardwareId.toLowerCase() === hardwareId);
      if (!device) {
        this.internalNotificationsStore.addNotification(
          `The NFC tag with hardware ID ${hardwareId} is not assigned to your farm. Please contact your mOOvement contact person.`,
          InternalNotificationType.ERROR,
          true
        );
        return;
      }

      const deviceId = device.id;

      const payload = event.tag.ndefMessage?.[0].payload;
      const hasValidHeader = !!payload && payload[0] === 0x50 && payload[1] === 0x47;
      if (!hasValidHeader) {
        this.processFailedScan(deviceId);
        if (!payload) {
          const techDetails = `${JSON.stringify(event.tag)}`
          this.displayUnreadableTagNotification(`No payload was found.`, techDetails);
        } else {
          this.displayIncompatibleTagNotification();
        }
        this.offerRetry();
        return;
      }

      this.device.set(device);
      const animal = this.animalStore.deviceIdToAnimal().get(deviceId);
      this.animal.set(animal);

      const sampleValues = toSampleValues(payload);
      this.discrepancyReason.set(sampleValues.discrepancyMessage);
      this.sampleValues.set(sampleValues.samples);
    });
  }

  // A misread is often just tag/device movement during the tap, so offer an easy way
  // to try again rather than silently retrying or forcing the user to hunt for the
  // "Detect and read" button.
  private async offerRetry() {
    if (confirm('This can happen if the tag or device moved during the scan. Try scanning again?')) {
      await this.startScan();
    }
  }

  private displayUnreadableTagNotification(reason: string, techDetails?: string) {
    this.internalNotificationsStore.addNotification(
      `The scanned NFC tag could not be read: ${reason}`,
      InternalNotificationType.ERROR,
      true,
      undefined,
      techDetails
    );
  }

  private async normalizedHardwareId(id: number[]): Promise<string> {
    const info = await CapacitorDevice.getInfo();
    const operatingSystem = info.operatingSystem;

    if (operatingSystem === 'android') id.reverse();

    return id.map(x => x.toString(16).padStart(2, '0')).join('');
  }

  private displayIncompatibleTagNotification() {
    this.internalNotificationsStore.addNotification(
      "The scanned NFC tag could not be recognized as a mOOvement tag. Please contact your mOOvement contact person.",
      InternalNotificationType.ERROR,
      true
    );
  }

  setName(event: Event) {
    const input = event.target as HTMLInputElement;
    this.name.set(input.value);
  }

  setAnimalType(animalType: AnimalType) {
    this.animalType.set(animalType);
  }

  setYear(year: number) {
    this.year.set(year.toString());
  }

  createAnimal() {

    const farmId = this.device()?.farmId;
    const deviceId = this.device()?.id;
    if (!this.canCreateAnimal() || !farmId || !deviceId) return;

    const animal = {
      id: randomUuid(),
      type: this.animalType() as AnimalType,
      yearOfBirth: this.year(),
      farmId: farmId,
      visualTag: this.name()
    };

    this.animal.set(animal);
    this.shouldCreateAnimal = true;
  }

  private async processFailedScan(deviceId: string) {
    const request = {
      deviceId,
      values: [],
      scannedAt: new Date().toISOString(),
      scanningDevice: await this.scanningDevice(),
      discrepancyReason: this.discrepancyReason(),
      failedScan: true
    };
    const updates: ScheduledUpdates = {
      id: randomUuid(),
      updates: [{
        type: "recordNfcLocationSamples",
        data: request
      }]
    };

    try {
      await this.dataSynchronisationService.scheduleUpdates(updates);
    } catch (error) {
      console.error('Failed to schedule failed-scan record:', error);
    }
  }

  async processLocations() {
    if (!this.canProcessLocations()) return;
    // const position: Position = {
    //   coords: [this.userLocationStore.latitude()!,this.userLocationStore.longitude()!]
    // };
    // const gpsFix: GPSFix = {
    //   position: position,
    //   accuracy: 0.5,
    //   timeToFix: 0,
    //   numberOfSatellites: 0
    // }

    const deviceId = this.device()?.id;
    const farmId = this.device()?.farmId;
    if (!deviceId || !farmId) return;

    const animal = this.animal();
    if (!animal) return;

    const updates: ScheduledUpdate[] = [];
    if (this.shouldCreateAnimal) {
      updates.push({
        type: "mergeAnimal",
        data: animal
      });
      updates.push({
        type: "associateDevice",
        data: {
          farmId,
          assetId: animal.id,
          deviceId: deviceId
        }
      });
    }

    updates.push({
      type: "recordNfcLocationSamples",
      data: {
        deviceId: deviceId,
        values: this.sampleValues(),
        scannedAt: new Date().toISOString(),
        scanningDevice: await this.scanningDevice(),
        discrepancyReason: this.discrepancyReason(),
        failedScan: false
      }
    });

    const name = animal.visualTag ?? animal.id;
    const scheduledUpdates: ScheduledUpdates = {
      id: randomUuid(),
      updates,
      successMessage: `Successfully processed locations for '${name}'`,
      scheduledMessage: `Locations for '${name}' will be processed when you're online again.`,
      failureMessage: `Failed to process locations for '${name}'.`
    };

    try {
      await this.dataSynchronisationService.scheduleUpdates(scheduledUpdates);
      this.reset();
    } catch (error) {
      console.error('Failed to schedule NFC location updates:', error);
      this.internalNotificationsStore.addNotification(
        `Failed to process locations for '${name}'. Please try again.`,
        InternalNotificationType.ERROR,
        true,
        JSON.stringify(error)
      );
    }
  }

  private async scanningDevice(): Promise<string> {
    const device = await CapacitorDevice.getInfo();
    const osVersion = device.osVersion

    return environment.appName + ' ' + appVersion + ' ' + device.platform + ' ' + osVersion;
  }

  protected async share() {
    await Share.share({
      text: JSON.stringify(this.sampleValues()),
    });
  }
}
